
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Database } from "@/integrations/supabase/types";
import { useProfile } from "@/hooks/useProfile";

type FixedJobBase = Database['public']['Views']['fixed_jobs_with_students']['Row'];

// Extended type with computed properties for backwards compatibility
export interface FixedJob extends Omit<FixedJobBase, 'id'> {
  id: number; // Override id to be number for backwards compatibility
  time_slot_id: string;
  time_slot_start_at: string;
  time_slot_end_at: string;
  time_slot_day: string;
  start_at: string;
  student_names: string;
  student_codes: string;
  subjects: string;
  education_level: string;
  class_type: string;
  course_type: string;
  branch: string;
  language: string;
  note: string | null;
  created_at: string;
  teacher_name: string;
}

// Helper to convert weekday number to day name
const weekdayToDay = (weekday: number): string => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[weekday] || 'unknown';
};

// Helper to convert UUID string to a numeric ID (for backwards compatibility)
const uuidToNumericId = (uuid: string): number => {
  // Simple hash function to convert UUID to number
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    const char = uuid.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

// Helper to transform database record to FixedJob
const transformToFixedJob = (record: FixedJobBase): FixedJob => {
  const students = (record.students as any) || [];
  const studentArray = Array.isArray(students) ? students : [];
  
  return {
    ...record,
    id: uuidToNumericId(record.id), // Convert UUID to number
    time_slot_id: record.id,
    time_slot_start_at: record.start_time,
    time_slot_end_at: record.end_time,
    time_slot_day: weekdayToDay(record.weekday),
    start_at: record.start_time,
    student_names: studentArray.map((s: any) => `${s.first_name || ''} ${s.last_name || ''}`.trim()).join(', ') || '',
    student_codes: studentArray.map((s: any) => s.code).filter(Boolean).join(', ') || '',
    subjects: record.subject,
    education_level: '',
    class_type: '',
    course_type: '',
    branch: record.location,
    language: '',
    note: null,
    created_at: new Date().toISOString(),
    teacher_name: ''
  };
};

export const useFixedJobSearch = (teacherCode?: string, refreshTrigger = 0) => {
  const [fixedJobs, setFixedJobs] = useState<FixedJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { profile } = useProfile();

  useEffect(() => {
    // Use teacher_id from profile if no specific teacherCode is provided
    const effectiveTeacherCode = teacherCode || profile?.teacher_id;
    
    if (!effectiveTeacherCode) {
      setFixedJobs([]);
      return;
    }

    const fetchJobs = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("fixed_jobs_with_students")
          .select("*")
          .eq("teacher_code", effectiveTeacherCode)
          .eq("is_active", true)
          .order("start_time", { ascending: true });
 
        if (error) {
          console.error("Error fetching fixed jobs:", error);
          toast({
            title: "Error",
            description: "Could not fetch fixed job data. Please try again.",
            variant: "destructive",
          });
          return;
        }
  
        // Transform the data to match the expected structure
        const transformedData = (data || []).map(transformToFixedJob);
        setFixedJobs(transformedData);
      } catch (error) {
        console.error("Error in API request:", error);
        toast({
          title: "Error",
          description: "Connection error. Please check your network.",
          variant: "destructive",
        });
        setFixedJobs([]);
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchJobs();
  }, [teacherCode, profile?.teacher_id, refreshTrigger]);

  return {
    fixedJobs,
    isLoading
  };
};
